import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import BusinessTwoToneIcon from "@mui/icons-material/BusinessTwoTone";
import {
	Box,
	Button,
	Card,
	Container,
	Typography
} from "@mui/material";
import React, { useEffect, useState } from "react";
import { Helmet } from "react-helmet-async";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import GlobalLoader from "src/components/SuspenseLoader/GlobalLoader";
import { useApiQuery } from "src/hooks/useApiQuery";
import type { IOfflineExhibitorDetails } from "../../type/offlineExhibitorType";
import DeleteOfflineExhibitor from "../DeleteOfflineExhibitor";
import UpdateOfflineExhibitor from "../UpdateOfflineExhibitor";
import CompactExhibitorOverview from "./CompactExhibitorOverview";
import ExhibitorScannedVisitorsTab from "./ExhibitorScannedVisitorsTab";
import ExhibitorServicesTab from "./ExhibitorServicesTab";
import ExhibitorStallsTab from "./ExhibitorStallsTab";
import ExhibitorTabsHeader from "./ExhibitorTabsHeader";

const VALID_TABS = ["stalls", "services", "scanned_visitors", "visitors", "scans"];

const SingleOfflineExhibitor: React.FC = () => {
	const { id, singleMemberFairId } = useParams<{
		id?: string;
		singleMemberFairId?: string;
	}>();
	const exhibitorId = Number(id || singleMemberFairId);
	const navigate = useNavigate();
	const [searchParams, setSearchParams] = useSearchParams();

	const urlTab = searchParams.get("tab");
	const [activeTab, setActiveTab] = useState<string>(() => {
		if (urlTab && VALID_TABS.includes(urlTab)) {
			return urlTab === "visitors" || urlTab === "scans"
				? "scanned_visitors"
				: urlTab;
		}
		const savedTab = localStorage.getItem("offline_exhibitor_active_tab");
		if (savedTab && VALID_TABS.includes(savedTab)) {
			return savedTab === "visitors" || savedTab === "scans"
				? "scanned_visitors"
				: savedTab;
		}
		return "stalls";
	});

	useEffect(() => {
		if (urlTab && VALID_TABS.includes(urlTab)) {
			const normalizedTab =
				urlTab === "visitors" || urlTab === "scans"
					? "scanned_visitors"
					: urlTab;
			setActiveTab(normalizedTab);
			localStorage.setItem("offline_exhibitor_active_tab", normalizedTab);
		}
	}, [urlTab]);

	const handleTabChange = (_event: React.SyntheticEvent, newTab: string) => {
		setActiveTab(newTab);
		localStorage.setItem("offline_exhibitor_active_tab", newTab);
		setSearchParams({ tab: newTab });
	};

	const [openEditModal, setOpenEditModal] = useState<boolean>(false);
	const [openDeleteModal, setOpenDeleteModal] = useState<boolean>(false);

	const { data: exhibitor, isLoading } = useApiQuery<IOfflineExhibitorDetails>({
		endPoint: `/api/v1/admin/offline-exhibitor/${exhibitorId}`,
		queryKeys: ["OfflineExhibitorDetails", exhibitorId],
		enabled: Boolean(exhibitorId),
	});

	if (isLoading) {
		return <GlobalLoader />;
	}

	const totalScanned =
		exhibitor?.total_scanned_stalls ??
		exhibitor?.total_scanned_stall ??
		exhibitor?.scan_visitors?.length ??
		0;

	return (
		<>
			<Helmet>
				<title>
					{exhibitor?.name
						? `BTTF - ${exhibitor.name} Details`
						: "BTTF - Exhibitor Details"}
				</title>
			</Helmet>

			<Container maxWidth="xl" sx={{ mt: 2.5, mb: 4 }}>
				{/* Top Navigation Bar */}
				<Box
					sx={{
						display: "flex",
						justifyContent: "space-between",
						alignItems: "center",
						mb: 2,
					}}
				>
					<Button
						variant="outlined"
						startIcon={<ArrowBackIcon />}
						onClick={() => navigate("/exhibitor/list")}
						sx={{
							borderColor: "grey.400",
							color: "text.primary",
							"&:hover": { backgroundColor: "grey.100" },
							borderRadius: 2,
							textTransform: "none",
							fontWeight: 600,
						}}
					>
						Back to List
					</Button>
				</Box>

				{!exhibitor ? (
					<Card sx={{ p: 5, textAlign: "center" }}>
						<BusinessTwoToneIcon
							sx={{ fontSize: 54, color: "text.disabled", mb: 1 }}
						/>
						<Typography variant="h4" color="text.secondary">
							Exhibitor not found or has been removed.
						</Typography>
					</Card>
				) : (
					<Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
						{/* Compact Modular Overview Card */}
						<CompactExhibitorOverview
							exhibitor={exhibitor}
							totalScanned={totalScanned}
							onEdit={() => setOpenEditModal(true)}
							onDelete={() => setOpenDeleteModal(true)}
						/>

						{/* Tabbed Section for Stalls, Services & Scanned Visitors */}
						<Card elevation={2} sx={{ overflow: "hidden", borderRadius: 2.5 }}>
							<ExhibitorTabsHeader
								activeTab={activeTab}
								onTabChange={handleTabChange}
								stallCount={exhibitor.stalls?.length || 0}
								serviceCount={exhibitor.services?.length || 0}
								scanCount={exhibitor.scan_visitors?.length || 0}
							/>

							{/* Stalls Tab */}
							{activeTab === "stalls" && (
								<ExhibitorStallsTab
									stalls={exhibitor.stalls}
									exhibitorName={exhibitor.name}
								/>
							)}

							{/* Services Tab */}
							{activeTab === "services" && (
								<ExhibitorServicesTab services={exhibitor.services} />
							)}

							{/* Scanned Visitors Tab */}
							{activeTab === "scanned_visitors" && (
								<ExhibitorScannedVisitorsTab
									visitors={exhibitor.scan_visitors}
									exhibitorName={exhibitor.name}
								/>
							)}
						</Card>
					</Box>
				)}

				{/* Update Modal */}
				{openEditModal && (
					<UpdateOfflineExhibitor
						open={openEditModal}
						handleClose={() => setOpenEditModal(false)}
						exhibitorId={exhibitorId}
					/>
				)}

				{/* Delete Modal */}
				{openDeleteModal && (
					<DeleteOfflineExhibitor
						open={openDeleteModal}
						handleClose={() => {
							setOpenDeleteModal(false);
							navigate("/exhibitor/list");
						}}
						exhibitorId={exhibitorId}
						exhibitorName={exhibitor?.name}
					/>
				)}
			</Container>
		</>
	);
};

export default SingleOfflineExhibitor;
